/**
 * Lector del panel de estadísticas: muestrea `getStats()` una vez por segundo y
 * destila lo que importa para juzgar la calidad del enlace a ojo.
 *
 * getStats() devuelve un diccionario de informes sin tipar apenas; aquí se palpa
 * cada campo con cuidado en vez de confiar en la forma.
 */

export interface VideoStats {
  fps: number | null;
  bitrateKbps: number | null;
  rttMs: number | null;
  packetsLost: number | null;
  width: number | null;
  height: number | null;
  codec: string | null;
}

export type StatsListener = (stats: VideoStats) => void;

interface StatsSource {
  getStats(): Promise<RTCStatsReport>;
}

function num(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

export function startStatsLoop(source: StatsSource, listener: StatsListener): () => void {
  let previousBytes: number | null = null;
  let previousTimestamp: number | null = null;
  let stopped = false;

  const tick = async (): Promise<void> => {
    const report = await source.getStats();

    let inbound: Record<string, unknown> | null = null;
    let candidatePair: Record<string, unknown> | null = null;
    const codecs = new Map<string, string>();

    report.forEach((entry: unknown) => {
      if (typeof entry !== 'object' || entry === null) return;
      const record = entry as Record<string, unknown>;
      const type = str(record, 'type');

      if (type === 'inbound-rtp' && str(record, 'kind') === 'video') {
        inbound = record;
      }
      // La pareja de candidatos activa: nominated + succeeded. Es donde vive el
      // RTT medido de verdad (STUN), no una estimación.
      if (
        type === 'candidate-pair' &&
        record['nominated'] === true &&
        str(record, 'state') === 'succeeded'
      ) {
        candidatePair = record;
      }
      if (type === 'codec') {
        const id = str(record, 'id');
        const mime = str(record, 'mimeType');
        if (id !== null && mime !== null) codecs.set(id, mime);
      }
    });

    const stats: VideoStats = {
      fps: null,
      bitrateKbps: null,
      rttMs: null,
      packetsLost: null,
      width: null,
      height: null,
      codec: null,
    };

    if (inbound !== null) {
      const record = inbound as Record<string, unknown>;
      stats.fps = num(record, 'framesPerSecond');
      stats.packetsLost = num(record, 'packetsLost');
      stats.width = num(record, 'frameWidth');
      stats.height = num(record, 'frameHeight');

      const codecId = str(record, 'codecId');
      if (codecId !== null) {
        const mime = codecs.get(codecId);
        if (mime !== undefined) stats.codec = mime.replace('video/', '');
      }

      const bytes = num(record, 'bytesReceived');
      const timestamp = num(record, 'timestamp');
      if (bytes !== null && timestamp !== null) {
        if (previousBytes !== null && previousTimestamp !== null && timestamp > previousTimestamp) {
          const deltaSeconds = (timestamp - previousTimestamp) / 1000;
          stats.bitrateKbps = ((bytes - previousBytes) * 8) / 1000 / deltaSeconds;
        }
        previousBytes = bytes;
        previousTimestamp = timestamp;
      }
    }

    if (candidatePair !== null) {
      const rtt = num(candidatePair as Record<string, unknown>, 'currentRoundTripTime');
      if (rtt !== null) stats.rttMs = rtt * 1000;
    }

    listener(stats);
  };

  const interval = setInterval(() => {
    if (stopped) return;
    void tick().catch(() => {
      // getStats sobre una conexión cerrándose puede rechazar; el bucle se está
      // parando de todas formas.
    });
  }, 1000);

  return () => {
    stopped = true;
    clearInterval(interval);
  };
}
